// avatar-pick.js — shared avatar file-picker: the downscale pipeline
// (fileToAvatarDataUrl) plus the DOM wiring around it (bindAvatarPicker),
// extracted from pages/login.js (C1496) so the signup wizard's profile step
// and pages/profile.js (TPT81) share one implementation instead of two
// copies drifting apart. This module touches the DOM (Image/canvas/
// URL.createObjectURL) — unlike lib/signup-form.js, it is not node-testable,
// which is why it lives in a separate file from that pure sibling.

import { dataUrlByteLength, MAX_AVATAR_BYTES } from './signup-form.js';

const AVATAR_TARGET_DIM = 256;
const AVATAR_QUALITY_STEPS = [0.85, 0.7, 0.55, 0.4];

// Reads an image file, center-crops it to a square, downsizes it to
// AVATAR_TARGET_DIM, and re-encodes as JPEG — stepping quality down through
// AVATAR_QUALITY_STEPS until the decoded size clears the server's 512 KB cap
// (api/src/lib/avatar.js) — so an ordinary phone photo (3-5 MB) doesn't just
// bounce off the 413. This is a UX affordance only; the server's own
// magic-byte + size check (api/src/lib/avatar.js) is the real boundary.
//
// JPEG has no alpha channel, so a transparent PNG is composited onto white
// first — otherwise transparent areas render solid black once re-encoded.
// The center-crop to a square matters because renderAvatarHtml() (utils.js)
// emits a bare `<img class="avatar">`: the stylesheet's `.avatar img`
// object-fit:cover rule targets an img *nested inside* a `.avatar` element,
// not one that IS `.avatar` itself, so it does not apply here — an
// uncropped rectangular source would be stretched (the browser's default
// object-fit: fill), not cropped, to fit the circle.
export async function fileToAvatarDataUrl(file) {
  if (!file.type || !file.type.startsWith('image/')) {
    throw new Error('That file is not an image');
  }
  if (file.size > 20 * 1024 * 1024) {
    throw new Error('That image is too large to process');
  }

  const objectUrl = URL.createObjectURL(file);
  let img;
  try {
    img = new Image();
    img.src = objectUrl;
    await img.decode();
  } catch {
    throw new Error('Could not read that image');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }

  const side = Math.min(img.naturalWidth, img.naturalHeight);
  const sx = (img.naturalWidth - side) / 2;
  const sy = (img.naturalHeight - side) / 2;

  const canvas = document.createElement('canvas');
  canvas.width = AVATAR_TARGET_DIM;
  canvas.height = AVATAR_TARGET_DIM;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, AVATAR_TARGET_DIM, AVATAR_TARGET_DIM);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, AVATAR_TARGET_DIM, AVATAR_TARGET_DIM);

  for (const quality of AVATAR_QUALITY_STEPS) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrlByteLength(dataUrl) <= MAX_AVATAR_BYTES) return dataUrl;
  }
  throw new Error('Could not compress that image below 500 KB — try a smaller photo');
}

// Wires an avatar-drop-zone circle + hidden file input + optional remove
// button to fileToAvatarDataUrl(), covering every entry point (click-to-pick,
// drag-and-drop, remove) with the same validation/error path. The caller owns
// state and rendering via three callbacks:
//   onPick(dataUrl)  — a file was picked/dropped and downscaled successfully
//   onRemove()       — the remove button was clicked
//   onError(message) — validation/downscale failed ('' clears a prior error)
//
// `preview` gets the click handler, the drag-over/leave/drop handlers, and
// the .drag-over class toggle. `form`, when given, also gets a blanket
// dragover/drop preventDefault — without it, a drop that misses the circle
// but lands elsewhere on the form navigates the browser to the dropped file,
// wiping whatever the user had typed.
export function bindAvatarPicker({ preview, input, removeBtn, form, onPick, onRemove, onError }) {
  // Shared by the file-input `change` handler and the drop handler below, so
  // both paths run the exact same validation/downscale/error copy.
  const applyPickedFile = async (file) => {
    onError('');
    let dataUrl;
    try {
      dataUrl = await fileToAvatarDataUrl(file);
    } catch (err) {
      onError(err.message);
      return;
    }
    onPick(dataUrl);
    if (removeBtn) {
      // `.btn` sets display:inline-flex at the same specificity as the UA
      // [hidden] rule and wins on cascade order (see the existing
      // .pwa-install-btn[hidden] override in styles.css) — toggle via inline
      // style, not the `hidden` attribute, to actually show/hide this button.
      removeBtn.style.display = '';
    }
  };

  // Plain click()/change — no cancel-event/window-focus fallback like
  // lib/embed-menu.js's openPicker(). That fallback exists solely to
  // release a *caller's* `pickerOpen` boolean when a dismissed dialog never
  // fires 'change'; this picker keeps no such flag, so there is nothing to
  // release and copying the fallback would be dead code.
  preview.addEventListener('click', () => input.click());
  input.addEventListener('change', () => {
    const file = input.files[0];
    input.value = ''; // allow re-picking the exact same file later
    if (!file) return;
    applyPickedFile(file);
  });
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      onRemove();
      removeBtn.style.display = 'none';
    });
  }

  // Drag-and-drop onto the same circle, same validation path as the file
  // input (lib/image-paste.js:86-99 is the app's only other native-DnD
  // file-drop precedent — board drags are mouse-based, not applicable here).
  preview.addEventListener('dragover', (e) => {
    e.preventDefault();
    preview.classList.add('drag-over');
  });
  preview.addEventListener('dragleave', (e) => {
    // preview has a child (the avatar/placeholder element) — unlike
    // image-paste.js's childless <textarea>, a bare dragleave here would
    // flicker off every time the cursor crosses onto that child.
    if (preview.contains(e.relatedTarget)) return;
    preview.classList.remove('drag-over');
  });
  preview.addEventListener('drop', (e) => {
    e.preventDefault();
    preview.classList.remove('drag-over');
    const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (!file) return;
    // Non-image types fail inside applyPickedFile's existing
    // fileToAvatarDataUrl() check ("That file is not an image") — no
    // separate type check needed here.
    applyPickedFile(file);
  });

  if (form) {
    // A drop that misses the circle but lands elsewhere on the form would
    // otherwise navigate the browser to the dropped file, wiping whatever the
    // user had typed — make the whole form inert to it instead.
    form.addEventListener('dragover', (e) => e.preventDefault());
    form.addEventListener('drop', (e) => e.preventDefault());
  }
}
